import { LoadingSpinner } from "components/LoadingSpinner";
import { mapPayerNameToCodeForDropdown } from "constants/practiceType";
import useFormValidation from "hooks/useFormValidation";
import { sizer } from "layout/styles/styled/sizer";
import { Label } from "layout/typography/Label";
import { isEmpty } from "lodash";
import { handleChangeDateWithMask } from "pages/CreateOrder/utils/mask";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import styled from "styled-components";
import { IAccount } from "types/Account/Account";
import {
  IOrder,
  IGFEServiceDetails,
  IFacilityRules,
  INonFacilityRules,
} from "types/Order/Order";
import { IProvider } from "types/Provider/Provider";

import { BenefitsSecondary } from "./Benefits/BenefitsSecondary";
import { EligibilityButton } from "./EligibilityButton";

const StyledEligibilityButtonContainer = styled.div`
  @media (min-width: 992px) {
    margin-top: 28px;
  }
`;

const StyledMessageContainer = styled.div`
  border: 1px solid var(--gray-new-200);
  border-left: none;
  border-right: none;
  background-color: #f9fafb;
  padding: 22px 24px;
  color: #dc6803;
  display: flex;
  align-items: center;
  gap: ${sizer(2)};
`;

export function SecondaryInsuranceSection({
  loadingPayersName,
  payersNameInfo,
  payersCodesInfo,
  redo = false,
  draft = false,
  copy = false,
  isEligibilityEnabledForMainProvider,
  accountId,
  mainProvider,
  costApplicationRules,
  account,
  draftOrder,
  orderToRedo,
  orderToCopy,
  handleRemoveSecondaryInsurance,
}: {
  loadingPayersName: boolean;
  payersNameInfo: any[];
  payersCodesInfo: any[];
  redo?: boolean;
  draft?: boolean;
  copy?: boolean;
  isEligibilityEnabledForMainProvider?: boolean;
  accountId: string;
  mainProvider: IProvider;
  costApplicationRules?: {
    facilityRules: IFacilityRules;
    nonFacilityRules: INonFacilityRules;
  };
  account: IAccount;
  draftOrder?: IOrder;
  orderToRedo?: IOrder;
  orderToCopy?: IOrder;
  handleRemoveSecondaryInsurance: () => void;
}) {
  const {
    control,
    trigger,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  const [eligibilityMessage, setEligibilityMessage] = useState<string | null>(
    null
  );
  const [showMemberIdAndEligibility, setShowMemberIdAndEligibility] =
    useState<boolean>(true);

  const { handleOnBlurField, getFormErrorMessage } = useFormValidation();
  const payerName2Field = watch("payerName2");
  const payer2Field = watch("payer2");
  const patientFirstNameField = watch("firstName");
  const patientLastNameField = watch("lastName");
  const patientDOBField = watch("dateOfBirth");
  const memberId2Field = watch("memberId2");
  const practiceType2Field = watch("practiceType2");
  const isSubscriberPatientCheckbox = watch("isSubscriberPatient");
  const benefitDetails2Field = watch("benefitDetails2");

  useEffect(() => {
    if ((payer2Field?.name && payer2Field?.payerId) || loadingPayersName)
      return;
    const applyPayer = (payerName: string, payerId: number) => {
      setValue("payerName2", payerName);
      setValue("payer2", { name: payerName, payerId });
    };

    const getPayerFromServiceDetails = (payerName?: string) => {
      if (!payerName && isEmpty(payersNameInfo)) {
        return { name: null, payerId: null };
      }
      const name = payerName || payersNameInfo[0]?.value?.name;
      const payerId =
        payerName &&
        payersNameInfo?.find((payer: any) => payer.name === payerName)?.value
          .payerId
          ? payersNameInfo.find((payer: any) => payer.name === payerName)?.value
              .payerId
          : payersNameInfo[0].value.payerId;

      return { name, payerId };
    };

    const processOrder = (order: any) => {
      const savedPayerId = order?.secondaryPayerId;
      if (savedPayerId) {
        applyPayer(order?.secondaryPayer?.name, order?.secondaryPayer?.id);
      } else {
        const payerNameFromDetails = (
          order?.serviceDetails as IGFEServiceDetails
        )?.secondaryPayerName;
        const { name, payerId } =
          getPayerFromServiceDetails(payerNameFromDetails);
        if (!name && !payerId) {
          return;
        }
        applyPayer(name, payerId);
      }
    };
    if (redo) {
      processOrder(orderToRedo);
    } else if (copy) {
      processOrder(orderToCopy);
    } else if (draft) {
      processOrder(draftOrder);
    } else {
      applyPayer(
        payersNameInfo[0]?.value?.name,
        payersNameInfo[0]?.value?.payerId
      );
    }
  }, [payer2Field, payersNameInfo, redo, copy, draft]);

  useEffect(() => {
    if (payerName2Field === "Self-Pay Rate") {
      setShowMemberIdAndEligibility(false);
      setValue("deductible2", 0);
      setValue("outOfPocketMax2", 100000);
      setValue("flatCopay2", 0);
      setValue("coinsurancePercent2", 100);
    } else {
      setShowMemberIdAndEligibility(true);
    }
  }, [payerName2Field]);

  // Default practiceType2 to "All Other Medical" when secondary insurance is added
  useEffect(() => {
    if (!practiceType2Field && mapPayerNameToCodeForDropdown.length > 0) {
      setValue("practiceType2", mapPayerNameToCodeForDropdown[0].value);
    }
  }, [practiceType2Field, setValue]);

  // Set eligibility message based on stored benefitDetails2 planStatus
  // NOTE: Don't clear the message if recommendedPayerBasedOnEligibility is present,
  // as that message is set by EligibilityButton and should persist (CLE-1221).
  useEffect(() => {
    const response = benefitDetails2Field?.response;
    if (!response) {
      return;
    }
    // If we detected Medicare Advantage and recommended a payer swap, don't overwrite that message
    if (response.recommendedPayerBasedOnEligibility) {
      return;
    }
    if (response.planStatus === "Inactive") {
      setEligibilityMessage(
        "The patient coverage shows inactive, please contact the patient or the payer to obtain most current information"
      );
    } else {
      const allFieldsAreNull =
        response.familyCalendarYearDeductible === null &&
        response.individualCalendarYearDeductible === null &&
        response.remainingDeductible === null &&
        response.coinsurancePercent === null;
      if (response.isEligibilityEmpty || allFieldsAreNull) {
        setEligibilityMessage(response.additionalInfo);
      } else {
        setEligibilityMessage(null);
      }
    }
  }, [benefitDetails2Field]);

  useEffect(() => {
    const draftOrderCheck =
      draft &&
      (!draftOrder?.secondaryBenefitDetails ||
        !draftOrder?.secondaryBenefitDetails?.response);
    const redoOrderCheck =
      redo &&
      (!orderToRedo?.secondaryBenefitDetails ||
        !orderToRedo?.secondaryBenefitDetails?.response);
    const orderToCopyCheck =
      copy &&
      (!orderToCopy?.secondaryBenefitDetails ||
        !orderToCopy?.secondaryBenefitDetails?.response);
    if (
      loadingPayersName ||
      draftOrderCheck ||
      redoOrderCheck ||
      orderToCopyCheck ||
      (!draft && !redo && !copy)
    ) {
      setValue("benefitDetails2", null);
      return;
    }
    let order = orderToRedo;
    if (draft) {
      order = draftOrder;
    } else if (copy) {
      order = orderToCopy;
    }
    const providerNpi = mainProvider?.npi;
    const eligibilityEnabled = mainProvider?.eligibilityEnabled;
    const eligibilityRequest = order?.secondaryBenefitDetails?.request;
    if (
      eligibilityRequest?.member?.firstName !== patientFirstNameField ||
      eligibilityRequest?.member?.lastName !== patientLastNameField ||
      eligibilityRequest?.member?.dob !== patientDOBField ||
      eligibilityRequest?.member?.memberID !== memberId2Field ||
      eligibilityRequest?.practiceTypeCode !== practiceType2Field ||
      eligibilityRequest?.provider?.npi !== providerNpi ||
      eligibilityRequest?.isSubscriberPatient !== eligibilityEnabled
    ) {
      setValue("benefitDetails2", null);
    } else {
      setValue("benefitDetails2", order?.secondaryBenefitDetails);
      if (redo || draft || copy) {
        const response = order?.secondaryBenefitDetails?.response;
        if (response.planStatus === "Inactive") {
          setEligibilityMessage(
            "The patient coverage shows inactive, please contact the patient or the payer to obtain most current information"
          );
        }
        const allFieldsAreNull =
          response.familyCalendarYearDeductible === null &&
          response.individualCalendarYearDeductible === null &&
          response.remainingDeductible === null &&
          response.coinsurancePercent === null;
        if (response.isEligibilityEmpty || allFieldsAreNull) {
          setEligibilityMessage(response.additionalInfo);
        }
      }
    }
  }, [
    patientFirstNameField,
    patientLastNameField,
    patientDOBField,
    memberId2Field,
    practiceType2Field,
    loadingPayersName,
    payersCodesInfo,
    draftOrder,
    orderToRedo,
    orderToCopy,
    mainProvider,
  ]);

  return (
    <div className="flex flex-column gap-2 mt-4">
      <div className="flex justify-content-end px-3">
        <Button
          label="Remove Secondary Insurance"
          icon="pi pi-trash"
          className="p-button-danger p-button-outlined"
          onClick={handleRemoveSecondaryInsurance}
        />
      </div>
      <div className="formgrid grid flex-column lg:flex-row px-3">
        <div className="field flex flex-column lg:col-9 col-12">
          <div className="formgrid grid">
            <div className="field flex flex-column col-12 lg:col-4">
              <Label htmlFor="payer2">Payer Name</Label>

              <Controller
                control={control}
                name="payer2"
                render={({ field: { onChange, value } }) =>
                  loadingPayersName ? (
                    <LoadingSpinner
                      customStyle={{ width: "50px", height: "100px" }}
                    />
                  ) : (
                    <Dropdown
                      value={value || ""}
                      optionLabel="name"
                      placeholder="Any"
                      id="payer2"
                      data-testid="payer2_PayerForm"
                      options={payersNameInfo}
                      onChange={(e) => {
                        onChange(e.value);
                        setValue("payerName2", e.value.name);
                        if (redo) {
                          setValue("redoOrderFirstRender", false);
                        }
                        if (draft) {
                          setValue("draftOrderFirstRender", false);
                        }
                        if (copy) {
                          setValue("copyOrderFirstRender", false);
                        }
                      }}
                    />
                  )
                }
              />
            </div>
            {isEligibilityEnabledForMainProvider && (
              <div className="field flex flex-column col-12 lg:col-4">
                <Label htmlFor="practiceType2">Service Type</Label>

                <Controller
                  control={control}
                  name="practiceType2"
                  render={({ field: { onChange, value } }) => (
                    <Dropdown
                      value={value || ""}
                      optionLabel="name"
                      data-testid="practiceType2_PayerForm"
                      placeholder="Service Type"
                      id="practiceType2"
                      options={mapPayerNameToCodeForDropdown}
                      onChange={onChange}
                    />
                  )}
                />
              </div>
            )}
            {isEligibilityEnabledForMainProvider &&
              showMemberIdAndEligibility && (
                <div className="field flex flex-column col-12 lg:col-4">
                  <Label htmlFor="memberId2">Member ID</Label>

                  <Controller
                    control={control}
                    name="memberId2"
                    render={({ field: { onChange, value, onBlur } }) => (
                      <>
                        <InputText
                          id="memberId2"
                          value={value || ""}
                          autoComplete="off"
                          data-testid="memberId2_PayerForm"
                          placeholder="Member ID"
                          onBlur={() =>
                            handleOnBlurField({
                              onBlur,
                              field: "memberId2",
                              trigger,
                            })
                          }
                          onChange={onChange}
                        />

                        {getFormErrorMessage("memberId2", errors)}
                      </>
                    )}
                  />
                </div>
              )}
          </div>
          {isEligibilityEnabledForMainProvider &&
            !isSubscriberPatientCheckbox && (
              <div className="formgrid grid">
                <div className="field flex flex-column col-12 lg:col-4">
                  <Label htmlFor="subscriberFirstName2">
                    Subscriber First Name
                  </Label>

                  <Controller
                    control={control}
                    name="subscriberFirstName2"
                    render={({ field: { onChange, value, onBlur } }) => (
                      <>
                        <InputText
                          id="subscriberFirstName2"
                          data-testid="subscriberFirstName2_PayerForm"
                          value={value || ""}
                          autoComplete="off"
                          placeholder="Subscriber Name"
                          onBlur={() =>
                            handleOnBlurField({
                              onBlur,
                              field: "subscriberFirstName2",
                              trigger,
                            })
                          }
                          onChange={onChange}
                        />

                        {getFormErrorMessage("subscriberFirstName2", errors)}
                      </>
                    )}
                  />
                </div>
                <div className="field flex flex-column col-12 lg:col-4">
                  <Label htmlFor="subscriberLastName2">
                    Subscriber Last Name
                  </Label>

                  <Controller
                    control={control}
                    name="subscriberLastName2"
                    render={({ field: { onChange, value, onBlur } }) => (
                      <>
                        <InputText
                          id="subscriberLastName2"
                          data-testid="subscriberLastName2_PayerForm"
                          value={value || ""}
                          autoComplete="off"
                          placeholder="Subscriber Name"
                          onBlur={() =>
                            handleOnBlurField({
                              onBlur,
                              field: "subscriberLastName2",
                              trigger,
                            })
                          }
                          onChange={onChange}
                        />

                        {getFormErrorMessage("subscriberLastName2", errors)}
                      </>
                    )}
                  />
                </div>
                <div className="field flex flex-column col-12 lg:col-4">
                  <Label htmlFor="subscriberDOB2">Subscriber DOB</Label>

                  <Controller
                    name="subscriberDOB2"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <>
                        <InputText
                          value={value || ""}
                          id="subscriberDOB2"
                          data-testid="subscriberDOB2_PayerForm"
                          placeholder="99/99/9999"
                          onBlur={() =>
                            handleOnBlurField({
                              onBlur,
                              field: "subscriberDOB2",
                              trigger,
                            })
                          }
                          onChange={(e) =>
                            handleChangeDateWithMask(e, onChange)
                          }
                        />

                        {getFormErrorMessage("subscriberDOB2", errors)}
                      </>
                    )}
                  />
                </div>
              </div>
            )}
          {isEligibilityEnabledForMainProvider && (
            <div className="field flex-column col-12 md:col-12 lg:col-3 hidden">
              <Label htmlFor="groupNumber2">Group Number</Label>

              <Controller
                control={control}
                name="groupNumber2"
                render={({ field: { onChange, value, onBlur } }) => (
                  <>
                    <InputText
                      id="groupNumber2"
                      data-testid="groupNumber2_PayerForm"
                      value={value || ""}
                      autoComplete="off"
                      placeholder="Group Number"
                      onBlur={() =>
                        handleOnBlurField({
                          onBlur,
                          field: "groupNumber2",
                          trigger,
                        })
                      }
                      onChange={onChange}
                    />

                    {getFormErrorMessage("groupNumber2", errors)}
                  </>
                )}
              />
            </div>
          )}
        </div>
        <StyledEligibilityButtonContainer className="field flex col-3">
          {showMemberIdAndEligibility ? (
            <EligibilityButton
              accountId={accountId}
              mainProvider={mainProvider}
              errorMessageSetter={setEligibilityMessage}
              disabled={loadingPayersName}
              isSecondaryInsurance
            />
          ) : null}
        </StyledEligibilityButtonContainer>
      </div>
      <StyledMessageContainer>
        <i className="pi pi-info-circle" style={{ fontSize: "20px" }} />
        <p className="text-md font-semibold">
          Confirm benefits are correct by checking all location-specific options
        </p>
      </StyledMessageContainer>
      <BenefitsSecondary
        redo={redo}
        draft={draft}
        copy={copy}
        costApplicationRules={costApplicationRules}
        account={account}
        eligibilityMessage={eligibilityMessage}
      />
    </div>
  );
}
